import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import {
  BookOpen,
  Brain,
  Users,
  Layout,
  Layers,
  Heart,
  BarChart,
  Calendar,
  MapPin,
  CheckCircle,
  Linkedin,
  Twitter,
  Instagram,
  ArrowRight,
  Quote } from
'lucide-react';
import { useContent } from '../hooks/useContent';

const iconMap = {
  BookOpen,
  Brain,
  Users,
  Layout,
  Layers,
  Heart,
  BarChart
};

const LandingPage = () => {
  const navigate = useNavigate();
  const { content, loading, error } = useContent();

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner">Loading...</div>
      </div>);

  }

  if (error) {
    return (
      <div className="error-container">
        <p>Error loading content: {error}</p>
      </div>);

  }

  if (!content) return null;

  const {
    hero: heroData,
    valuePropositions,
    aboutRetreat,
    impactData,
    policyAlignment,
    eligibility,
    registrationOpenEvents,
    participants,
    facilitators,
    completedEvents,
    testimonials
  } = content;

  const handleRequestInvite = () => {
    window.open('https://survey.zohopublic.in/zs/PyNtNi', '_blank');
  };

  return (
    <div className="landing-page">
      {/* Hero Section */}
      <section className="hero-section">
        <video 
          className="hero-video-background"
          autoPlay 
          loop 
          muted 
          playsInline
        >
          <source src="https://customer-assets.emergentagent.com/job_strange-montalcini/artifacts/7ozji6it_edit.mp4" type="video/mp4" />
        </video>
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <Badge className="inline-flex items-center border transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 border-transparent hover:bg-primary/80 hero-badge !font-semibold !text-2xl px-2.5 py-0.5 !rounded-[9px] shadow text-primary-foreground bg-primary">Transform Education</Badge>
          <h1 className="hero-headline">{heroData.headline}</h1>
          <h2 className="hero-subheadline">{heroData.subheadline}</h2>
          <div className="hero-quote">
            <Quote className="quote-icon" />
            <p>{heroData.quote}</p>
          </div>
          <div className="hero-cta">
            <Button onClick={handleRequestInvite} className="btn-primary">
              Request an Invite
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button variant="outline" className="btn-secondary">
              Explore the Retreat
            </Button>
          </div>
        </div>
      </section>

      {/* Value Proposition Section */}
      <section className="section value-proposition">
        <div className="container">
          <div className="value-grid">
            {valuePropositions.map((item) => {
              const IconComponent = iconMap[item.icon];
              return (
                <Card key={item.id} className="value-card">
                  <CardHeader>
                    <div className="icon-wrapper">
                      <IconComponent className="value-icon" />
                    </div>
                    <CardTitle>{item.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription>{item.description}</CardDescription>
                  </CardContent>
                </Card>);

            })}
          </div>
        </div>
      </section>

      {/* About Retreat Section */}
      <section id="about" className="section about-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">{aboutRetreat.heading}</h2>
            <p className="section-description">{aboutRetreat.description}</p>
          </div>

          <div className="focus-areas-grid">
            {aboutRetreat.focusAreas.map((area) => {
              const IconComponent = iconMap[area.icon];
              return (
                <div key={area.id} className="focus-card">
                  <IconComponent className="focus-icon" />
                  <h3 className="focus-title">{area.title}</h3>
                  <p className="focus-description">{area.description}</p>
                </div>);

            })}
          </div>

          <div className="reflection-quote">
            <Quote className="quote-mark" />
            <p>{aboutRetreat.reflectionQuote}</p>
          </div>
        </div>
      </section>

      {/* Impact Section */}
      <section className="section impact-section">
        <div className="container">
          <div className="impact-content">
            <div className="impact-text">
              <h2 className="section-title">{impactData.heading}</h2>
              <p className="impact-message">{impactData.message}</p>
              <ul className="impact-list">
                {impactData.keyPoints.map((point, index) =>
                <li key={index}>
                    <CheckCircle className="check-icon" />
                    <span>{point}</span>
                  </li>
                )}
              </ul>
            </div>
            <div className="impact-visual">
              <div className="visual-placeholder">
                <Users className="placeholder-icon" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Policy Alignment Section */}
      <section className="section policy-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">{policyAlignment.heading}</h2>
            <p className="section-description">{policyAlignment.intro}</p>
          </div>

          <div className="policy-grid">
            {policyAlignment.nepHighlights.map((item, index) =>
            <Card key={index} className="policy-card">
                <CardHeader>
                  <CardTitle className="policy-card-title">{item.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="policy-card-description">{item.description}</p>
                </CardContent>
              </Card>
            )}
          </div>

          <div className="leadership-emphasis">
            <p>{policyAlignment.leadershipEmphasis}</p>
          </div>
        </div>
      </section>

      {/* Eligibility Section */}
      <section className="section eligibility-section">
        <div className="container">
          <h2 className="section-title">{eligibility.heading}</h2>
          <div className="eligibility-content">
            <div className="requirements-list">
              {eligibility.requirements.map((req, index) =>
              <div key={index} className="requirement-item">
                  <CheckCircle className="req-icon" />
                  <span>{req}</span>
                </div>
              )}
            </div>
            <Badge className="infrastructure-badge">{eligibility.infrastructureNote}</Badge>
          </div>
          <div className="invite-note">
            <p>{eligibility.inviteNote}</p>
          </div>
        </div>
      </section>

      {/* Upcoming Events Section */}
      <section className="section events-section">
        <div className="container">
          <h2 className="section-title">Upcoming Events</h2>
          <p className="section-description">Join us for these transformative learning experiences</p>
          <div className="events-grid">
            {registrationOpenEvents.slice(0, 2).map((event) =>
            <Card key={event.id} className="event-card">
                <CardHeader>
                  <Badge className="event-status">{event.status}</Badge>
                  <CardTitle>{event.title}</CardTitle>
                  <CardDescription>{event.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="event-details">
                    <div className="event-detail-item">
                      <Calendar className="detail-icon" />
                      <span>{event.date}</span>
                    </div>
                    <div className="event-detail-item">
                      <MapPin className="detail-icon" />
                      <span>{event.location}</span>
                    </div>
                    <div className="event-detail-item">
                      <Users className="detail-icon" />
                      <span>{event.format}</span>
                    </div>
                  </div>
                  <Button onClick={handleRequestInvite} className="event-cta">
                    Request Invite
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
          <div className="events-cta-section">
            <Button
              onClick={() => navigate('/events')}
              className="view-all-events-btn"
              variant="outline">

              View All Events
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Facilitators Section */}
      <section id="facilitators" className="section facilitators-section">
        <div className="container">
          <h2 className="section-title">Keynote Facilitators</h2>
          <div className="facilitators-grid">
            {facilitators.map((facilitator) =>
            <Card key={facilitator.id} className="facilitator-card">
                <CardHeader>
                  <img
                  src={facilitator.image}
                  alt={facilitator.name}
                  className="facilitator-image" />

                  <CardTitle className="facilitator-name">{facilitator.name}</CardTitle>
                  <p className="facilitator-role">{facilitator.role}</p>
                  <Badge className="expertise-badge">{facilitator.expertise}</Badge>
                </CardHeader>
                <CardContent>
                  <p className="facilitator-description">{facilitator.description}</p>
                  <div className="social-links">
                    {facilitator.linkedin && (
                      <a href={facilitator.linkedin} target="_blank" rel="noopener noreferrer" className="social-link">
                        <Linkedin className="social-icon" />
                      </a>
                    )}
                    {facilitator.instagram && (
                      <a href={facilitator.instagram} target="_blank" rel="noopener noreferrer" className="social-link">
                        <Instagram className="social-icon" />
                      </a>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </section>

      {/* Participants Section */}
      <section id="participants" className="section participants-section">
        <div className="container">
          <h2 className="section-title">Past Participants</h2>
          <div className="participants-grid">
            {participants.map((participant) =>
            <Card key={participant.id} className="participant-card">
                <CardHeader>
                  <img
                  src={participant.image}
                  alt={participant.name}
                  className="participant-image" />

                  <CardTitle className="participant-name">{participant.name}</CardTitle>
                  <p className="participant-designation">{participant.designation}</p>
                  <p className="participant-school">{participant.school}</p>
                </CardHeader>
                <CardContent>
                  <p className="participant-bio">{participant.bio}</p>
                  <div className="social-links">
                    <a href={participant.linkedin} className="social-link">
                      <Linkedin className="social-icon" />
                    </a>
                    <a href={participant.twitter} className="social-link">
                      <Twitter className="social-icon" />
                    </a>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </section>

      {/* Past Events Section */}
      <section className="section past-events-section">
        <div className="container">
          <h2 className="section-title">Past Events</h2>
          <div className="past-events-grid">
            {completedEvents.slice(0, 2).map((event) =>
            <Card key={event.id} className="past-event-card">
                <div className="event-images">
                  <img src={event.images[0]} alt={event.title} className="main-image" />
                  <div className="thumbnail-grid">
                    {event.images.slice(1).map((img, idx) =>
                  <img key={idx} src={img} alt={`${event.title} ${idx + 1}`} className="thumbnail" />
                  )}
                  </div>
                </div>
                <CardHeader>
                  <CardTitle>{event.title}</CardTitle>
                  <div className="event-meta">
                    <span>{event.date}</span>
                    <Separator orientation="vertical" className="meta-separator" />
                    <span>{event.location}</span>
                  </div>
                </CardHeader>
                <CardContent>
                  <p>{event.description}</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="section testimonials-section">
        <div className="container">
          <h2 className="section-title">What Leaders Say</h2>
          <div className="testimonials-grid">
            {testimonials.map((testimonial) =>
            <Card key={testimonial.id} className="testimonial-card">
                <CardContent className="testimonial-content">
                  <Quote className="testimonial-quote-icon" />
                  <p className="testimonial-text">{testimonial.quote}</p>
                  <div className="testimonial-author">
                    <p className="author-name">{testimonial.name}</p>
                    <p className="author-school">{testimonial.school}</p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="section final-cta-section">
        <div className="container">
          <div className="final-cta-content">
            <h2 className="final-cta-headline">Ready to Transform Your School?</h2>
            <p className="final-cta-subtext">
              Join visionary leaders in creating inclusive, equitable learning environments
            </p>
            <div className="final-cta-buttons">
              <Button onClick={handleRequestInvite} className="btn-primary-large">
                Request an Invite
                <ArrowRight className="ml-2 h-6 w-6" />
              </Button>
              <Button variant="outline" className="btn-secondary-large">
                Contact Us
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>);

};

export default LandingPage;